import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { SlidersHorizontal } from 'lucide-react';

export interface MetricColumn {
  id: string;
  label: string;
  visible: boolean;
}

interface MetricsColumnFilterProps {
  columns: MetricColumn[];
  onColumnsChange: (columns: MetricColumn[]) => void;
}

export function MetricsColumnFilter({ columns, onColumnsChange }: MetricsColumnFilterProps) {
  useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const toggleColumn = (columnId: string) => {
    const updatedColumns = columns.map(col => 
      col.id === columnId ? { ...col, visible: !col.visible } : col
    );
    onColumnsChange(updatedColumns);
  };

  const showAll = () => {
    const updatedColumns = columns.map(col => ({ ...col, visible: true }));
    onColumnsChange(updatedColumns);
  };

  const hideAll = () => {
    const updatedColumns = columns.map(col => ({ ...col, visible: false }));
    onColumnsChange(updatedColumns);
  };

  const visibleCount = columns.filter(col => col.visible).length;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          className="h-10 gap-2 px-3.5 text-[13px] text-[#6B7280] border-[#E5E7EB] bg-white hover:bg-white hover:text-[#6B7280] hover:border-[#E5E7EB] focus:ring-0 rounded-[10px] transition-colors cursor-pointer"
          style={{ boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.04)' }}
        >
          <SlidersHorizontal className="h-4 w-4 text-[#6B7280]" />
          {translateInterfaceText("Colunas (")}{visibleCount}/{columns.length})
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[280px] p-0 border-[#DFE7FF] rounded-[12px] bg-white" 
        align="end"
        style={{ boxShadow: '0px 12px 26px rgba(0, 0, 0, 0.06)' }}
      >
        <div className="py-3">
          {/* Header */}
          <div className="flex items-center justify-between px-4 pb-3 border-b border-[#EDF1FF]">
            <h4 className="font-semibold text-[13px] text-[#2C3345]">{translateInterfaceText("Colunas visíveis")}</h4>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2.5 text-[12px] text-[#4B7BFF] hover:bg-[#F3F6FF] hover:text-[#4B7BFF] rounded-lg transition-colors"
                onClick={showAll}
              >
                {translateInterfaceText("Todas")}</Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2.5 text-[12px] text-[#6E7A95] hover:bg-[#F3F6FF] hover:text-[#475067] rounded-lg transition-colors"
                onClick={hideAll}
              >
                {translateInterfaceText("Nenhuma")}</Button>
            </div>
          </div>
          
          {/* Column list */}
          <div className="pt-2 max-h-[400px] overflow-y-auto">
            {columns.map((column) => (
              <div 
                key={column.id} 
                className="flex items-center space-x-3 px-4 py-2.5 hover:bg-[#F3F6FF] transition-all duration-150 cursor-pointer"
                onClick={() => toggleColumn(column.id)}
              >
                <Checkbox
                  id={column.id}
                  checked={column.visible}
                  onCheckedChange={() => toggleColumn(column.id)}
                  className="border-[#CBD4E4] data-[state=checked]:bg-[#4B7BFF] data-[state=checked]:border-[#4B7BFF] rounded-[4px]"
                />
                <Label
                  htmlFor={column.id}
                  className="text-[13px] font-medium text-[#475067] cursor-pointer leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex-1"
                >
                  {column.label}
                </Label>
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
